import type { PersonaProvisioningResult } from './personaProvisioningRuntime';

export const PERSONA_PROVISIONING_REASON_MISSING =
  'persona-provisioning-reason-missing';
export const NO_PERSONA_INTENT_FOR_FEDERATED_USER =
  'no_persona_intent_for_federated_user';

export type PersonaProvisioningCompletion =
  | {
      kind: 'claim_refresh';
      targetPersona: string;
    }
  | {
      kind: 'failure';
      failureReason: string;
      message: string;
    };

export const normalizePersonaProvisioningFailureReason = (
  value: string | null | undefined,
  fallback = PERSONA_PROVISIONING_REASON_MISSING,
): string => {
  const trimmed = String(value ?? '').trim();
  return trimmed.length > 0 ? trimmed : fallback;
};

const describeProvisioningFailure = (reason: string): string => {
  switch (reason) {
    case 'staff_signup_pending_approval':
      return 'School staff access for this account is pending approval.';
    case 'super-administrator-not-allowlisted':
      return 'This Intellio account is not allowlisted for YSTNJ administrator access.';
    case 'super-administrator-requires-google':
      return 'Administrator access requires Google sign-in for this Intellio account.';
    case 'super-administrator-requires-verified-google':
      return 'Administrator access requires a verified Google email for this Intellio account.';
    case 'super-administrator-domain-mismatch':
      return 'Administrator access is limited to approved Intellio accounts.';
    case 'no_matching_assignment_rule':
      return 'This account does not have a matching YSTNJ role assignment.';
    case NO_PERSONA_INTENT_FOR_FEDERATED_USER:
      return 'Google sign-in completed, but no persona selection or existing Cognito role was available for this account.';
    case 'persona-provisioning-no-result':
      return 'YSTNJ did not receive a usable role assignment result for this session.';
    case PERSONA_PROVISIONING_REASON_MISSING:
      return 'YSTNJ marked persona provisioning as failed without returning a concrete reason.';
    default:
      return `YSTNJ could not assign a role for this session (${reason}).`;
  }
};

const BLOCKED_SUPER_ADMIN_REASONS = new Set([
  'super-administrator-not-allowlisted',
  'super-administrator-requires-google',
  'super-administrator-requires-verified-google',
  'super-administrator-domain-mismatch',
]);

export const describeClaimRefreshFailure = (reason: string): string =>
  `Role claims did not become available for this session (${reason}).`;

export const resolvePersonaProvisioningCompletion = (
  result: PersonaProvisioningResult | null,
  options?: {
    hasPersonaIntent?: boolean;
    isFederatedProvider?: boolean;
  },
): PersonaProvisioningCompletion => {
  if (!result) {
    const failureReason = 'persona-provisioning-no-result';
    return {
      kind: 'failure',
      failureReason,
      message: describeProvisioningFailure(failureReason),
    };
  }

  const backendReason = normalizePersonaProvisioningFailureReason(
    result.reason,
    result.status === 'pending_staff_approval'
      ? 'staff_signup_pending_approval'
      : 'no_matching_assignment_rule',
  );

  if (BLOCKED_SUPER_ADMIN_REASONS.has(backendReason)) {
    return {
      kind: 'failure',
      failureReason: backendReason,
      message: describeProvisioningFailure(backendReason),
    };
  }

  if (result.status === 'assigned' || result.status === 'already_assigned') {
    return {
      kind: 'claim_refresh',
      targetPersona: result.assignedGroup || result.persona || 'parent',
    };
  }

  const failureReason =
    options?.isFederatedProvider
    && !options.hasPersonaIntent
    && backendReason === 'no_matching_assignment_rule'
      ? NO_PERSONA_INTENT_FOR_FEDERATED_USER
      : backendReason;
  return {
    kind: 'failure',
    failureReason,
    message: describeProvisioningFailure(failureReason),
  };
};
